import type { Obtained, Unit, UnitKind } from "@sins2-atlas/game-data";

// The roster facet-filter URL contract (ADR 0002: everything in the URL — the schema is a
// public API). Each facet is a comma-separated list param: absent means "no filter". Tokens
// are player-facing words, stable across patches, and serialization is always in canonical
// order so equal filters produce equal URLs. Terminology is the game's own (CONTEXT.md).
//
// These facets are PER VIEW, which is what this module is: Class and Obtained on Ships,
// Slot type on Structures. Race and Faction used to live here too and no longer do — the
// Faction Selection is site-wide (ADR 0010) and lives in lib/faction-selection.ts, read by
// the shell, both roster views, /tech, /compare and the unit search.
//
// Unknown tokens never error a shared link, but they are never silently swallowed either
// (ADR 0002 amendment 2026-07-30, matching the rule /compare already followed). They stay
// out of the typed filters AND are reported by `parseUnknownFacetTokens`, so a facet the URL
// asked for is *active* even when nothing in it is recognizable — `?class=bogus` matches no
// unit rather than quietly returning the whole roster under a URL that promised a filter.

// Class filter tokens follow the game's class ladder (CONTEXT.md: Class; dataset field
// `kind`), one token per rung.
export const CLASS_TOKEN_BY_KIND = {
  strikecraft: "strikecraft",
  corvette: "corvette",
  frigate: "frigate",
  cruiser: "cruiser",
  capital_ship: "capital-ship",
  super_capital_ship: "command-ship",
  titan: "titan",
  structure: "structure",
  starbase: "starbase",
} as const satisfies Record<UnitKind, string>;
export type ClassToken = (typeof CLASS_TOKEN_BY_KIND)[UnitKind];
export const CLASS_TOKENS = Object.values(CLASS_TOKEN_BY_KIND) as ClassToken[];

export const OBTAINED_TOKEN_BY_VALUE = {
  direct_build: "build-menu",
  hangar_built: "hangar",
  ability_created: "ability",
  diplomacy: "diplomacy",
  auto_built: "auto-built",
} as const satisfies Record<Obtained, string>;
export type ObtainedToken = (typeof OBTAINED_TOKEN_BY_VALUE)[Obtained];
export const OBTAINED_TOKENS = Object.values(OBTAINED_TOKEN_BY_VALUE) as ObtainedToken[];

// The Slot type facet's tokens (Issue 29, CONTEXT.md: Orbit Slot): the game's two build
// groups, whose names (`structure_build_group_name_*`, "CIVILIAN" / "MILITARY") ship in the
// dataset and label the options. Structures only — a ship consumes no slot, so the Ships
// schema never carries the key.
export const SLOT_TOKENS = ["civilian", "military"] as const;
export type SlotToken = (typeof SLOT_TOKENS)[number];

/** Which build group a unit files under: its slot kind for a structure, and Military for a
 * starbase — its virtual slot is a military one, and the player files list it under the
 * military build group (CONTEXT.md: Structure). Undefined for a ship. */
export function slotTokenOf(unit: Pick<Unit, "structure">): SlotToken | undefined {
  switch (unit.structure?.slotType) {
    case "civilian":
      return "civilian";
    case "military":
    case "virtual":
      return "military";
    case undefined:
      return undefined;
  }
}

export interface RosterFilters {
  class: ClassToken[];
  obtained: ObtainedToken[];
  slot: SlotToken[];
}

/** The raw search-param surface the filters serialize to. */
export interface RosterFilterSearch {
  class?: string;
  obtained?: string;
  slot?: string;
}

/** Parse one CSV param against its canonical token list: duplicates collapse and the result
 * keeps canonical order regardless of URL order. */
function parseCsv<T extends string>(value: string | undefined, canonical: readonly T[]): T[] {
  if (value === undefined || value === "") return [];
  const requested = new Set(value.split(","));
  return canonical.filter((token) => requested.has(token));
}

/** The tokens of one CSV param that the vocabulary does not recognize, deduplicated and left
 * in the order the URL gave them — this list is shown back to the reader verbatim. */
function parseCsvUnknown(value: string | undefined, canonical: readonly string[]): string[] {
  if (value === undefined || value === "") return [];
  return [...new Set(value.split(","))].filter(
    (token) => token !== "" && !canonical.includes(token),
  );
}

function serializeCsv(tokens: readonly string[]): string | undefined {
  return tokens.length === 0 ? undefined : tokens.join(",");
}

export function parseRosterFilters(search: RosterFilterSearch): RosterFilters {
  return {
    class: parseCsv(search.class, CLASS_TOKENS),
    obtained: parseCsv(search.obtained, OBTAINED_TOKENS),
    slot: parseCsv(search.slot, SLOT_TOKENS),
  };
}

/** Per facet, the tokens the URL asked for that no vocabulary recognizes. */
export interface UnknownFacetTokens {
  class: string[];
  obtained: string[];
  slot: string[];
}

export const NO_UNKNOWN_TOKENS: UnknownFacetTokens = {
  class: [],
  obtained: [],
  slot: [],
};

export function parseUnknownFacetTokens(search: RosterFilterSearch): UnknownFacetTokens {
  return {
    class: parseCsvUnknown(search.class, CLASS_TOKENS),
    obtained: parseCsvUnknown(search.obtained, OBTAINED_TOKENS),
    slot: parseCsvUnknown(search.slot, SLOT_TOKENS),
  };
}

export function hasUnknownTokens(unknown: UnknownFacetTokens): boolean {
  return unknown.class.length > 0 || unknown.obtained.length > 0 || unknown.slot.length > 0;
}

export function serializeRosterFilters(filters: RosterFilters): RosterFilterSearch {
  return {
    class: serializeCsv(filters.class),
    obtained: serializeCsv(filters.obtained),
    slot: serializeCsv(filters.slot),
  };
}

export const NO_FILTERS: RosterFilters = {
  class: [],
  obtained: [],
  slot: [],
};

export function hasActiveFilters(filters: RosterFilters): boolean {
  return filters.class.length > 0 || filters.obtained.length > 0 || filters.slot.length > 0;
}

/** A facet the URL did not ask for means "all"; a facet it did ask for keeps units matching
 * ANY of its recognized tokens. The Faction Selection is applied separately and site-wide
 * (lib/faction-selection.ts). `unknown` is required rather than defaulted: a facet asked for with only
 * unrecognizable tokens is still ACTIVE and matches nothing, and omitting that argument would
 * silently restore the old "unknown token reads as no filter" behaviour. */
export function matchesFilters(
  unit: Unit,
  filters: RosterFilters,
  unknown: UnknownFacetTokens,
): boolean {
  if (
    filters.class.length + unknown.class.length > 0 &&
    !filters.class.includes(CLASS_TOKEN_BY_KIND[unit.kind])
  ) {
    return false;
  }
  if (
    filters.obtained.length + unknown.obtained.length > 0 &&
    !filters.obtained.includes(OBTAINED_TOKEN_BY_VALUE[unit.obtained])
  ) {
    return false;
  }
  if (filters.slot.length + unknown.slot.length > 0) {
    // A ship files under no slot kind, so an active Slot type facet never matches one.
    const slot = slotTokenOf(unit);
    if (slot === undefined || !filters.slot.includes(slot)) return false;
  }
  return true;
}

/** Toggle one token in a facet list, returning tokens in canonical order. */
export function toggleToken<T extends string>(
  current: readonly T[],
  token: T,
  canonical: readonly T[],
): T[] {
  const next = new Set(current);
  if (next.has(token)) next.delete(token);
  else next.add(token);
  return canonical.filter((t) => next.has(t));
}
